import React, { useEffect, useState, useMemo } from 'react';
import {
	Dialog,
	DialogContent,
	IconButton,
	Box,
	CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import matchService from '../services/matchService';
import { translateRound, translateLeague, translateTeamName } from '../utils/roundTranslator';
import { getDominantColor, deconflictColors, rgbToCss, readableOnLight } from '../utils/badgeColor';
import { FaFutbol, FaShieldAlt, FaChartBar, FaBalanceScale, FaStar, FaInfoCircle } from 'react-icons/fa';
import '../styles/PredictionModal.css';

const DEFAULT_HOME = '#2563eb';
const DEFAULT_AWAY = '#dc2626';
const DRAW_COLOR = '#6b7280';

const parsePercent = (p) => {
	if (p === null || p === undefined) return 0;
	const n = parseFloat(String(p).replace('%', ''));
	return isNaN(n) ? 0 : n;
};

const formResultClass = (r) => {
	if (r === 'W') return 'form-w';
	if (r === 'L') return 'form-l';
	if (r === 'D') return 'form-d';
	return 'form-n';
};

const buildFormArray = (formStr) => {
	if (!formStr || typeof formStr !== 'string') return [];
	return formStr.slice(-5).split('');
};

const MINUTE_BUCKETS = ['0-15', '16-30', '31-45', '46-60', '61-75', '76-90'];

// API-Football advice/comment strings jsou anglicky. Tahle funkce nahradí
// známé fráze českými ekvivalenty bez toho, aby přepsala názvy týmů.
const translateAfPhrase = (text) => {
	if (!text || typeof text !== 'string') return text;
	const replacements = [
		// Pořadí důležité – delší fráze nejprve, aby se neukousávaly části.
		[/\bCombo Double chance\b/gi, 'Kombinace dvojitá šance'],
		[/\bDouble chance\b/gi, 'Dvojitá šance'],
		[/\bWin or draw\b/gi, 'Výhra nebo remíza'],
		[/\bWinner\b/gi, 'Vítěz'],
		[/\bGoals Over\b/gi, 'Více branek než'],
		[/\bGoals Under\b/gi, 'Méně branek než'],
		[/\bOver\b/gi, 'Více než'],
		[/\bUnder\b/gi, 'Méně než'],
		[/\bdraw or\b/gi, 'remíza nebo'],
		[/\bor draw\b/gi, 'nebo remíza'],
		[/\bdraw\b/gi, 'remíza'],
		[/\bOR\b/g, 'nebo'],
		[/\band\b/gi, 'a'],
	];
	let result = text;
	replacements.forEach(([re, rep]) => {
		result = result.replace(re, rep);
	});
	return result;
};

const computeWinner = (percent) => {
	const home = parsePercent(percent?.home);
	const draw = parsePercent(percent?.draw);
	const away = parsePercent(percent?.away);
	const max = Math.max(home, draw, away);
	if (max === 0) return null;
	if (max === home) return 'home';
	if (max === away) return 'away';
	return 'draw';
};

const PredictionModal = ({ open, matchId, tournamentId, homeTeam, awayTeam, onClose, onCreditConsumed }) => {
	const { t } = useTranslation();
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [data, setData] = useState(null);
	const [teamColors, setTeamColors] = useState(null);

	// Týmové barvy (po deconfliktu, stejné jako na MatchCard) pro obarvení modálu.
	useEffect(() => {
		if (!open || !homeTeam?.id || !awayTeam?.id) return undefined;
		let cancelled = false;
		Promise.all([
			getDominantColor(homeTeam.id, homeTeam.name),
			getDominantColor(awayTeam.id, awayTeam.name),
		]).then(([h, a]) => {
			if (cancelled) return;
			setTeamColors(deconflictColors(h.palette, a.palette));
		});
		return () => { cancelled = true; };
	}, [open, homeTeam?.id, homeTeam?.name, awayTeam?.id, awayTeam?.name]);

	useEffect(() => {
		if (!open || !matchId) return;
		let cancelled = false;
		setLoading(true);
		setError(null);
		setData(null);

		matchService.getMatchPrediction(matchId, tournamentId)
			.then((res) => {
				if (cancelled) return;
				const payload = Array.isArray(res?.response) ? res.response[0] : (res?.response || res);
				setData(payload);
				// Predikce čerpá sdílený kredit – po úspěchu necháme rodiče obnovit jeho stav
				// a označit zápas jako odemčený (matchId).
				if (onCreditConsumed) onCreditConsumed(matchId);
			})
			.catch((err) => {
				if (cancelled) return;
				if (err?.response?.status === 403) {
					setError(err.response.data || t('matchCard.dailyLimitReached', { defaultValue: 'Denní limit pro turnaj vyčerpán!' }));
				} else {
					setError(err?.response?.data || t('predictionModal.loadFailed', { defaultValue: 'Nepodařilo se načíst predikci.' }));
				}
			})
			.finally(() => {
				if (!cancelled) setLoading(false);
			});

		return () => {
			cancelled = true;
		};
	}, [open, matchId, tournamentId, t, onCreditConsumed]);

	const homeLogo = data?.teams?.home?.logo || (homeTeam?.id ? `${process.env.REACT_APP_API_URL?.replace(/\/$/, '')}/badges/${homeTeam.id}.png` : null);
	const awayLogo = data?.teams?.away?.logo || (awayTeam?.id ? `${process.env.REACT_APP_API_URL?.replace(/\/$/, '')}/badges/${awayTeam.id}.png` : null);

	const predictions = data?.predictions;
	const teams = data?.teams;
	const comparison = data?.comparison;
	const h2h = data?.h2h || [];

	const winner = useMemo(() => computeWinner(predictions?.percent), [predictions]);

	const homeName = translateTeamName(data?.teams?.home?.name || homeTeam?.name || '', t);
	const awayName = translateTeamName(data?.teams?.away?.name || awayTeam?.name || '', t);

	const homeColor = teamColors ? rgbToCss(readableOnLight(teamColors.home)) : DEFAULT_HOME;
	const awayColor = teamColors ? rgbToCss(readableOnLight(teamColors.away)) : DEFAULT_AWAY;

	const homePct = parsePercent(predictions?.percent?.home);
	const drawPct = parsePercent(predictions?.percent?.draw);
	const awayPct = parsePercent(predictions?.percent?.away);

	const renderBar = (value, color) => (
		<div className="pm-bar-row">
			<div className="pm-bar">
				<div
					className="pm-bar-fill"
					style={{ width: `${Math.min(100, Math.max(0, value))}%`, background: color }}
				/>
			</div>
			<span className="pm-bar-value-outside" style={{ color }}>{Math.round(value)}%</span>
		</div>
	);

	const renderCompareBar = (homeValue, awayValue) => {
		const home = Math.max(0, Math.min(100, homeValue));
		const away = Math.max(0, Math.min(100, awayValue));
		return (
			<div className="pm-compare-bar-wrapper">
				<span className="pm-compare-label pm-compare-label-home">{Math.round(home)}%</span>
				<div className="pm-compare-bar">
					<div className="pm-compare-seg pm-compare-seg-home" style={{ width: `${home}%` }} />
					<div className="pm-compare-seg pm-compare-seg-away" style={{ width: `${away}%` }} />
				</div>
				<span className="pm-compare-label pm-compare-label-away">{Math.round(away)}%</span>
			</div>
		);
	};

	const formLabel = (r) => {
		if (r === 'W') return t('win', { defaultValue: 'V' });
		if (r === 'L') return t('lost', { defaultValue: 'P' });
		if (r === 'D') return t('draw', { defaultValue: 'R' });
		return '–';
	};

	const renderForm = (formStr) => {
		const arr = buildFormArray(formStr);
		if (arr.length === 0) {
			return <div className="pm-form-empty">{t('predictionModal.noForm', { defaultValue: 'Bez dat' })}</div>;
		}
		return (
			<div className="pm-form-row">
				{arr.map((r, idx) => (
					<span key={idx} className={`pm-form-pill ${formResultClass(r)}`}>{formLabel(r)}</span>
				))}
			</div>
		);
	};

	const describeGoals = (raw) => {
		if (raw === null || raw === undefined || raw === '') return null;
		const n = parseFloat(String(raw).replace(/[+\s]/g, ''));
		if (isNaN(n)) return null;
		if (n < 0) {
			const max = Math.floor(Math.abs(n));
			return { kind: 'max', value: max };
		}
		const min = Math.floor(n) + 1;
		return { kind: 'min', value: min };
	};

	const homeMinutes = useMemo(
		() => teams?.home?.league?.goals?.for?.minute || {},
		[teams]
	);
	const homeAgainstMinutes = useMemo(
		() => teams?.home?.league?.goals?.against?.minute || {},
		[teams]
	);

	const maxMinute = useMemo(() => {
		let m = 0;
		MINUTE_BUCKETS.forEach((b) => {
			const f = parsePercent(homeMinutes?.[b]?.percentage);
			const a = parsePercent(homeAgainstMinutes?.[b]?.percentage);
			if (f > m) m = f;
			if (a > m) m = a;
		});
		return Math.max(m, 1);
	}, [homeMinutes, homeAgainstMinutes]);

	const advice = predictions?.advice;
	const winnerName = predictions?.winner?.name;
	const goalsHome = predictions?.goals?.home;
	const goalsAway = predictions?.goals?.away;

	const predictedLabel = useMemo(() => {
		if (predictions?.winner?.name) {
			// Pokud je winner.name přesně název týmu, použij překlad týmů; jinak
			// (např. "Arsenal or Draw") přepiš spojky/klíčová slova.
			const raw = predictions.winner.name;
			const teamTranslated = translateTeamName(raw, t);
			if (teamTranslated !== raw) return teamTranslated;
			return translateAfPhrase(raw);
		}
		if (winner === 'home') return homeName;
		if (winner === 'away') return awayName;
		if (winner === 'draw') return t('predictionModal.draw', { defaultValue: 'Remíza' });
		return t('predictionModal.noPrediction', { defaultValue: 'Bez predikce' });
	}, [predictions, winner, homeName, awayName, t]);

	// Vítěz badge: barva závisí na tom, jestli model favorizuje domácí nebo hosty.
	// Primárně podle predictions.winner.id (matchneme s id týmu), fallback na vypočítaný winner.
	const winnerSide = useMemo(() => {
		const winnerId = predictions?.winner?.id;
		if (winnerId != null) {
			if (teams?.home?.id === winnerId) return 'home';
			if (teams?.away?.id === winnerId) return 'away';
		}
		return winner; // 'home' | 'away' | 'draw' | null
	}, [predictions, teams, winner]);

	const badgeColors = useMemo(() => {
		// Badge „PREDIKCE" obarvíme týmovou barvou vítěze (světlý tint + sytý text).
		const arr = winnerSide === 'home' ? teamColors?.home
			: winnerSide === 'away' ? teamColors?.away
			: null;
		if (arr) {
			const [r, g, b] = readableOnLight(arr);
			return {
				bg: `rgba(${r}, ${g}, ${b}, 0.08)`,
				border: `rgba(${r}, ${g}, ${b}, 0.30)`,
				text: `rgb(${r}, ${g}, ${b})`,
				label: `rgba(${r}, ${g}, ${b}, 0.85)`,
				icon: `rgb(${r}, ${g}, ${b})`,
			};
		}
		return { bg: '#f1f5f9', border: '#e2e8f0', text: '#1f2937', label: '#475569', icon: '#475569' };
	}, [winnerSide, teamColors]);

	// Minimální dataset pro vykreslení timeline – některé zápasy (např. začínající sezona)
	// mají kompletní null pole; bez tohohle by se vykreslil prázdný graf bez labelů.
	const hasMinuteData = (minutes, againstMinutes) =>
		MINUTE_BUCKETS.some(
			(b) => parsePercent(minutes?.[b]?.percentage) > 0 || parsePercent(againstMinutes?.[b]?.percentage) > 0
		);

	const calcMaxMinute = (minutes, againstMinutes) => {
		let m = 0;
		MINUTE_BUCKETS.forEach((b) => {
			const f = parsePercent(minutes?.[b]?.percentage);
			const a = parsePercent(againstMinutes?.[b]?.percentage);
			if (f > m) m = f;
			if (a > m) m = a;
		});
		return Math.max(m, 1);
	};

	const awayMinutes = useMemo(
		() => teams?.away?.league?.goals?.for?.minute || {},
		[teams]
	);
	const awayAgainstMinutes = useMemo(
		() => teams?.away?.league?.goals?.against?.minute || {},
		[teams]
	);
	const awayMaxMinute = useMemo(
		() => calcMaxMinute(awayMinutes, awayAgainstMinutes),
		[awayMinutes, awayAgainstMinutes]
	);

	const renderTimeline = (minutes, againstMinutes, max, teamColor) => (
		<>
			<div className="pm-timeline">
				{MINUTE_BUCKETS.map((b) => {
					const f = parsePercent(minutes?.[b]?.percentage);
					const a = parsePercent(againstMinutes?.[b]?.percentage);
					return (
						<div key={b} className="pm-timeline-col">
							<div className="pm-timeline-bars">
								<div
									className="pm-timeline-bar"
									style={{ height: `${(f / max) * 100}%`, background: teamColor }}
									title={`${t('predictionModal.goalsFor', { defaultValue: 'Vstřelené' })}: ${f}%`}
								>
									<span className="pm-timeline-pct">{f > 0 ? `${Math.round(f)}%` : ''}</span>
								</div>
								<div
									className="pm-timeline-bar"
									style={{ height: `${(a / max) * 100}%`, background: DRAW_COLOR }}
									title={`${t('predictionModal.goalsAgainst', { defaultValue: 'Obdržené' })}: ${a}%`}
								>
									<span className="pm-timeline-pct">{a > 0 ? `${Math.round(a)}%` : ''}</span>
								</div>
							</div>
							<div className="pm-timeline-label">{b}</div>
						</div>
					);
				})}
			</div>
			<div className="pm-timeline-legend">
				<span><i style={{ background: teamColor }} />{t('predictionModal.goalsFor', { defaultValue: 'Vstřelené' })}</span>
				<span><i style={{ background: DRAW_COLOR }} />{t('predictionModal.goalsAgainst', { defaultValue: 'Obdržené' })}</span>
			</div>
		</>
	);

	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth="md"
			fullWidth
			PaperProps={{ className: 'pm-paper' }}
			sx={{
				'& .MuiBackdrop-root': {
					backgroundColor: 'rgba(0, 0, 0, 0.6)',
					backdropFilter: 'blur(3px)',
				},
			}}
		>
			<IconButton onClick={onClose} className="pm-close" aria-label="close">
				<CloseIcon />
			</IconButton>
			<DialogContent className="pm-content" style={{ '--pm-home': homeColor, '--pm-away': awayColor }}>
				{loading && (
					<Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
						<CircularProgress />
					</Box>
				)}
				{!loading && error && (
					<div className="pm-error">{String(error)}</div>
				)}
				{!loading && !error && data && (
					<>
						<div className="pm-hero">
							<div className="pm-hero-league">
								{data?.league?.logo && (
									<img src={data.league.logo} alt={data.league.name} className="pm-league-logo" />
								)}
								<div>
									<div className="pm-league-name">{translateLeague(data?.league?.name, t)}</div>
								</div>
							</div>

							<div className="pm-hero-teams">
								<div className="pm-team-block">
									<div className="pm-team-name">{homeName}</div>
									{homeLogo && <img src={homeLogo} alt={homeName} className="pm-team-logo" />}
								</div>
								<div className="pm-vs">VS</div>
								<div className="pm-team-block">
									<div className="pm-team-name">{awayName}</div>
									{awayLogo && <img src={awayLogo} alt={awayName} className="pm-team-logo" />}
								</div>
							</div>

							<div
								className="pm-prediction-badge"
								style={{
									background: badgeColors.bg,
									borderColor: badgeColors.border,
									color: badgeColors.text,
								}}
							>
								<FaStar style={{ color: badgeColors.icon }} />
								<div>
									<div className="pm-prediction-label" style={{ color: badgeColors.label }}>{t('predictionModal.prediction', { defaultValue: 'PREDIKCE' })}</div>
									<div className="pm-prediction-value" style={{ color: badgeColors.text }}>{predictedLabel}</div>
									{predictions?.win_or_draw && (
										<div className="pm-prediction-sub" style={{ color: badgeColors.label }}>{t('predictionModal.doubleChance', { defaultValue: 'Double chance' })}</div>
									)}
								</div>
							</div>

							<div className="pm-percentages">
								<div className="pm-percent-col">
									<div className="pm-percent-label">{homeName}</div>
									<div className="pm-percent-value" style={{ color: homeColor }}>{Math.round(homePct)}%</div>
								</div>
								<div className="pm-percent-col">
									<div className="pm-percent-label">{t('predictionModal.draw', { defaultValue: 'Remíza' })}</div>
									<div className="pm-percent-value pm-draw">{Math.round(drawPct)}%</div>
								</div>
								<div className="pm-percent-col">
									<div className="pm-percent-label">{awayName}</div>
									<div className="pm-percent-value" style={{ color: awayColor }}>{Math.round(awayPct)}%</div>
								</div>
							</div>

							<div className="pm-stacked-bar">
								<div className="pm-stacked-seg" style={{ width: `${homePct}%`, background: homeColor }} />
								<div className="pm-stacked-seg" style={{ width: `${drawPct}%`, background: DRAW_COLOR }} />
								<div className="pm-stacked-seg" style={{ width: `${awayPct}%`, background: awayColor }} />
							</div>

							{advice && advice !== 'No predictions available' && (
								<div className="pm-advice">
									<FaStar /> {t('predictionModal.advice', { defaultValue: 'Doporučení' })}: {translateAfPhrase(advice)}
								</div>
							)}
						</div>

						<div className="pm-grid">
							<div className="pm-card">
								<div className="pm-card-title">
									<FaChartBar /> {t('predictionModal.teamForm', { defaultValue: 'Forma týmu' })} <span className="pm-card-sub">({t('predictionModal.last5', { defaultValue: 'posledních 5 zápasů v soutěži' })})</span>
								</div>

								<div className="pm-team-summary">
									{homeLogo && <img src={homeLogo} alt={homeName} className="pm-mini-logo" />}
									<span className="pm-team-summary-name">{homeName}</span>
									<span className="pm-team-summary-pct" style={{ color: homeColor }}>{teams?.home?.last_5?.form || '0%'}</span>
								</div>
								{renderForm(teams?.home?.league?.form)}
								<div className="pm-stat-line">
									<span>{t('predictionModal.attack', { defaultValue: 'Útok' })}</span>
									{renderBar(parsePercent(teams?.home?.last_5?.att), homeColor)}
								</div>
								<div className="pm-stat-line">
									<span>{t('predictionModal.defense', { defaultValue: 'Obrana' })}</span>
									{renderBar(parsePercent(teams?.home?.last_5?.def), homeColor)}
								</div>
								<div className="pm-mini-stats">
									<div className="pm-mini-stat">
										<span className="pm-mini-stat-label">{t('predictionModal.avgGoalsFor', { defaultValue: 'Ø vstřelené' })}</span>
										<span className="pm-mini-stat-value" style={{ color: homeColor }}>{teams?.home?.last_5?.goals?.for?.average ?? '-'}</span>
									</div>
									<div className="pm-mini-stat">
										<span className="pm-mini-stat-label">{t('predictionModal.avgGoalsAgainst', { defaultValue: 'Ø obdržené' })}</span>
										<span className="pm-mini-stat-value" style={{ color: homeColor }}>{teams?.home?.last_5?.goals?.against?.average ?? '-'}</span>
									</div>
								</div>

								<div className="pm-team-summary" style={{ marginTop: 16 }}>
									{awayLogo && <img src={awayLogo} alt={awayName} className="pm-mini-logo" />}
									<span className="pm-team-summary-name">{awayName}</span>
									<span className="pm-team-summary-pct" style={{ color: awayColor }}>{teams?.away?.last_5?.form || '0%'}</span>
								</div>
								{renderForm(teams?.away?.league?.form)}
								<div className="pm-stat-line">
									<span>{t('predictionModal.attack', { defaultValue: 'Útok' })}</span>
									{renderBar(parsePercent(teams?.away?.last_5?.att), awayColor)}
								</div>
								<div className="pm-stat-line">
									<span>{t('predictionModal.defense', { defaultValue: 'Obrana' })}</span>
									{renderBar(parsePercent(teams?.away?.last_5?.def), awayColor)}
								</div>
								<div className="pm-mini-stats">
									<div className="pm-mini-stat">
										<span className="pm-mini-stat-label">{t('predictionModal.avgGoalsFor', { defaultValue: 'Ø vstřelené' })}</span>
										<span className="pm-mini-stat-value" style={{ color: awayColor }}>{teams?.away?.last_5?.goals?.for?.average ?? '-'}</span>
									</div>
									<div className="pm-mini-stat">
										<span className="pm-mini-stat-label">{t('predictionModal.avgGoalsAgainst', { defaultValue: 'Ø obdržené' })}</span>
										<span className="pm-mini-stat-value" style={{ color: awayColor }}>{teams?.away?.last_5?.goals?.against?.average ?? '-'}</span>
									</div>
								</div>
							</div>

							<div className="pm-card">
								<div className="pm-card-title">
									<FaFutbol /> {t('predictionModal.strength', { defaultValue: 'Síla týmů' })}
								</div>

								<div className="pm-compare-block">
									<div className="pm-compare-row">{t('predictionModal.attack', { defaultValue: 'Útok' })}</div>
									{renderCompareBar(
										parsePercent(comparison?.att?.home),
										parsePercent(comparison?.att?.away)
									)}
								</div>

								<div className="pm-compare-block">
									<div className="pm-compare-row">{t('predictionModal.defense', { defaultValue: 'Obrana' })}</div>
									{renderCompareBar(
										parsePercent(comparison?.def?.home),
										parsePercent(comparison?.def?.away)
									)}
								</div>

								<div className="pm-compare-block">
									<div className="pm-compare-row">{t('predictionModal.poisson', { defaultValue: 'Poisson predikce' })}</div>
									{renderCompareBar(
										parsePercent(comparison?.poisson_distribution?.home),
										parsePercent(comparison?.poisson_distribution?.away)
									)}
								</div>

								<div className="pm-compare-legend">
									<span><i style={{ background: homeColor }} />{homeName}</span>
									<span><i style={{ background: awayColor }} />{awayName}</span>
								</div>
							</div>

							<div className="pm-card">
								<div className="pm-card-title">
									<FaShieldAlt /> {t('predictionModal.teamStats', { defaultValue: 'Statistiky týmu v soutěži' })}
								</div>
								<table className="pm-stats-table">
									<thead>
										<tr>
											<th>
												{homeLogo && <img src={homeLogo} alt={homeName} className="pm-mini-logo" />}
											</th>
											<th></th>
											<th>
												{awayLogo && <img src={awayLogo} alt={awayName} className="pm-mini-logo" />}
											</th>
										</tr>
									</thead>
									<tbody>
										<tr>
											<td style={{ color: homeColor }}>{teams?.home?.league?.goals?.for?.average?.total ?? '-'}</td>
											<td>{t('predictionModal.goalsFor', { defaultValue: 'Vstřelené góly' })}</td>
											<td style={{ color: awayColor }}>{teams?.away?.league?.goals?.for?.average?.total ?? '-'}</td>
										</tr>
										<tr>
											<td style={{ color: homeColor }}>{teams?.home?.league?.goals?.against?.average?.total ?? '-'}</td>
											<td>{t('predictionModal.goalsAgainst', { defaultValue: 'Obdržené góly' })}</td>
											<td style={{ color: awayColor }}>{teams?.away?.league?.goals?.against?.average?.total ?? '-'}</td>
										</tr>
										<tr>
											<td style={{ color: homeColor }}>{teams?.home?.league?.clean_sheet?.total ?? teams?.home?.clean_sheet?.total ?? '-'}</td>
											<td>{t('predictionModal.cleanSheets', { defaultValue: 'Čistá konta' })}</td>
											<td style={{ color: awayColor }}>{teams?.away?.league?.clean_sheet?.total ?? teams?.away?.clean_sheet?.total ?? '-'}</td>
										</tr>
										<tr>
											<td style={{ color: homeColor }}>{teams?.home?.league?.failed_to_score?.total ?? teams?.home?.failed_to_score?.total ?? '-'}</td>
											<td>{t('predictionModal.failedToScore', { defaultValue: 'Bez gólu' })}</td>
											<td style={{ color: awayColor }}>{teams?.away?.league?.failed_to_score?.total ?? teams?.away?.failed_to_score?.total ?? '-'}</td>
										</tr>
									</tbody>
								</table>
							</div>

							<div className="pm-card pm-score-card">
								<div className="pm-card-title">
									<FaStar /> {t('predictionModal.expectedGoals', { defaultValue: 'Očekávaný počet branek' })}
								</div>
								{(() => {
									const homeGoals = describeGoals(goalsHome);
									const awayGoals = describeGoals(goalsAway);
									if (!homeGoals && !awayGoals) {
										return (
											<div className="pm-score-sub">
												{t('predictionModal.noScorePrediction', { defaultValue: 'Predikce skóre není k dispozici' })}
											</div>
										);
									}
									const renderTeamGoals = (team, goals, color) => {
										if (!goals) return null;
										const labelKey = goals.kind === 'max'
											? 'predictionModal.maxGoals'
											: 'predictionModal.minGoals';
										const fallback = goals.kind === 'max'
											? 'Maximálně {{n}}'
											: 'Minimálně {{n}}';
										return (
											<div className="pm-goals-team">
												<div className="pm-goals-team-name">{team}</div>
												<div className="pm-goals-team-value" style={{ color }}>
													{t(labelKey, { defaultValue: fallback, n: goals.value })}
												</div>
											</div>
										);
									};
									return (
										<div className="pm-goals-grid">
											{renderTeamGoals(homeName, homeGoals, homeColor)}
											{renderTeamGoals(awayName, awayGoals, awayColor)}
										</div>
									);
								})()}
							</div>

							{hasMinuteData(homeMinutes, homeAgainstMinutes) && (
								<div className="pm-card pm-wide">
									<div className="pm-card-title">
										<FaChartBar /> {t('predictionModal.expectedGoalsTimelineHome', { defaultValue: 'Časová osa očekávaných gólů (domácí)' })}
									</div>
									{renderTimeline(homeMinutes, homeAgainstMinutes, maxMinute, homeColor)}
								</div>
							)}

							{hasMinuteData(awayMinutes, awayAgainstMinutes) && (
								<div className="pm-card pm-wide">
									<div className="pm-card-title">
										<FaChartBar /> {t('predictionModal.expectedGoalsTimelineAway', { defaultValue: 'Časová osa očekávaných gólů (hosté)' })}
									</div>
									{renderTimeline(awayMinutes, awayAgainstMinutes, awayMaxMinute, awayColor)}
								</div>
							)}

							{h2h.length > 0 && (
								<div className="pm-card pm-wide">
									<div className="pm-card-title">
										<FaBalanceScale /> {t('predictionModal.h2h', { defaultValue: 'Vzájemné zápasy' })}
									</div>
									<ul className="pm-h2h-list">
										{h2h.slice(0, 5).map((m) => {
											const d = m.fixture?.date ? new Date(m.fixture.date) : null;
											const dateStr = d ? `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}` : '';
											const home = translateTeamName(m.teams?.home?.name, t);
											const away = translateTeamName(m.teams?.away?.name, t);
											const homeId = m.teams?.home?.id;
											const awayId = m.teams?.away?.id;
											const homeWinner = m.teams?.home?.winner === true;
											const awayWinner = m.teams?.away?.winner === true;
											// Barva vítěze podle toho, kterým týmem je v aktuálním zápase
											// (id matchneme s teams.home.id / teams.away.id z hlavičky modálu).
											const colorForTeamId = (id) => {
												if (id === teams?.home?.id) return homeColor;
												if (id === teams?.away?.id) return awayColor;
												return undefined;
											};
											const homeWinnerColor = homeWinner ? colorForTeamId(homeId) : undefined;
											const awayWinnerColor = awayWinner ? colorForTeamId(awayId) : undefined;
											const sh = m.goals?.home;
											const sa = m.goals?.away;
											const leagueNameRaw = m.league?.name;
											const leagueNameTr = translateLeague(leagueNameRaw, t);
											const roundLabel = m.league?.round ? translateRound(m.league.round, leagueNameRaw, t) : '';
											const meta = leagueNameTr
												? (roundLabel
													? t('predictionModal.matchAt', { defaultValue: '{{league}} • {{round}}', league: leagueNameTr, round: roundLabel })
													: leagueNameTr)
												: roundLabel;
											return (
												<li key={m.fixture?.id} className="pm-h2h-row">
													{meta && <span className="pm-h2h-meta">{meta}</span>}
													<span className="pm-h2h-date">{dateStr}</span>
													<span
														className={`pm-h2h-team pm-h2h-home ${homeWinner ? 'pm-h2h-winner' : ''}`}
														style={homeWinnerColor ? { color: homeWinnerColor } : undefined}
													>
														{home}
													</span>
													<span className="pm-h2h-score">{sh != null ? sh : '-'} : {sa != null ? sa : '-'}</span>
													<span
														className={`pm-h2h-team pm-h2h-away ${awayWinner ? 'pm-h2h-winner' : ''}`}
														style={awayWinnerColor ? { color: awayWinnerColor } : undefined}
													>
														{away}
													</span>
												</li>
											);
										})}
									</ul>
								</div>
							)}

							<div className="pm-card pm-wide">
								<div className="pm-card-title">
									<FaInfoCircle /> {t('predictionModal.whyPrediction', { defaultValue: 'Proč tato predikce?' })}
								</div>
								<ul className="pm-reasons">
									<li>
										<FaShieldAlt /> {t('predictionModal.reasonDefense', {
											defaultValue: 'Obrana {{home}}: {{hd}}%, {{away}}: {{ad}}%.',
											home: homeName,
											away: awayName,
											hd: Math.round(parsePercent(comparison?.def?.home)),
											ad: Math.round(parsePercent(comparison?.def?.away))
										})}
									</li>
									<li>
										<FaFutbol /> {t('predictionModal.reasonAttack', {
											defaultValue: 'Útok {{home}}: {{ha}}%, {{away}}: {{aa}}%.',
											home: homeName,
											away: awayName,
											ha: Math.round(parsePercent(comparison?.att?.home)),
											aa: Math.round(parsePercent(comparison?.att?.away))
										})}
									</li>
									<li>
										<FaBalanceScale /> {t('predictionModal.reasonH2h', {
											defaultValue: 'H2H {{home}}: {{hh}}%, {{away}}: {{ah}}%.',
											home: homeName,
											away: awayName,
											hh: Math.round(parsePercent(comparison?.h2h?.home)),
											ah: Math.round(parsePercent(comparison?.h2h?.away))
										})}
									</li>
									{winnerName && (
										<li>
											<FaStar /> {t('predictionModal.reasonWinner', {
												defaultValue: 'Predikovaný vítěz: {{name}}.',
												name: winnerName
											})}
										</li>
									)}
								</ul>
							</div>
						</div>
					</>
				)}
			</DialogContent>
		</Dialog>
	);
};

export default PredictionModal;
